import type { ComboboxData } from "@mantine/core";
import {
  Box,
  Divider,
  Group,
  Paper,
  Select,
  Stack,
  Title
} from "@mantine/core";
import type { TablerIcon } from "@tabler/icons-react";
import { IconHelpText } from "../../../components/IconHelpText/IconHelpText";
import { LoadOrError } from "../../../components/LoadOrError/LoadOrError";

const DROPDOWN_LIMIT = 3000;
const DROPDOWN_LIMIT_HELP_TEXT = `Aus Performance-Gründen werden nicht alle Einträge direkt im Dropdown angezeigt. Bitte verwenden Sie die Suche (Suchbegriff im Dropdown eintippen), um alle Einträge zu erreichen.`;

interface FilterOptions {
  value?: number;
  label: string;
}

interface TileProps {
  title: string;
  icon?: TablerIcon;
  children: React.ReactNode;
  error?: Error | null;
  isLoading?: boolean;
  filterOptions?: Array<FilterOptions>;
  filterValue?: number;
  filterPlaceholder?: string;
  onFilterChange?: (newFilterValue?: number) => void;
}

function Tile({
  title,
  icon,
  children,
  error,
  isLoading,
  filterOptions,
  filterValue,
  filterPlaceholder,
  onFilterChange
}: TileProps) {
  function handleChange(newValue: string | null) {
    if (!onFilterChange) {
      return;
    }

    const changedFilterValue =
      newValue === "all-entries" || newValue === null
        ? undefined
        : Number(newValue);
    onFilterChange(changedFilterValue);
  }

  const filterChoices: ComboboxData = filterOptions
    ? filterOptions.map((option) => ({
        value: option.value?.toString() || "all-entries",
        label: option.label
      }))
    : [];

  const Icon = icon;

  return (
    <Paper className="Tile">
      <Stack h="100%">
        <Group justify="space-between">
          <Group gap="sm">
            {Icon && <Icon />}
            <Title mb={0} order={6} size="h4">
              {title}
            </Title>
          </Group>
          {filterOptions && filterOptions.length > 0 && (
            <Group gap="xs">
              <Select
                aria-label={filterPlaceholder}
                data={filterChoices}
                limit={DROPDOWN_LIMIT}
                placeholder={filterPlaceholder}
                value={filterValue?.toString() || "all-entries"}
                w={300}
                onChange={handleChange}
              />
              {filterChoices.length > DROPDOWN_LIMIT && (
                <IconHelpText helpText={DROPDOWN_LIMIT_HELP_TEXT} />
              )}
            </Group>
          )}
        </Group>
        <Divider />
        <Box h="100%">
          <LoadOrError error={error} loading={isLoading}>
            {children}
          </LoadOrError>
        </Box>
      </Stack>
    </Paper>
  );
}

export { Tile, TileProps };
